import type { MentionContext, MentionResolver, ResolvedMention } from '../types'
import { formatCents } from '@/lib/slash-commands/utils'

type CampaignRow = {
  id: string
  name: string
  status: string
  budget: number | null
}

function scoreCampaign(name: string, query: string): number {
  const n = name.toLowerCase()
  const q = query.toLowerCase()
  if (!q) return 50
  if (n === q) return 100
  if (n.startsWith(q)) return 80 - (n.length - q.length)
  if (n.includes(q)) return 55
  // Fuzzy: subsequence
  let qi = 0
  for (const ch of n) {
    if (ch === q[qi]) qi++
    if (qi === q.length) break
  }
  if (qi === q.length) return 30 - (n.length - q.length)
  return 0
}

function makeSubtitle(row: CampaignRow): string {
  const parts: string[] = [row.status]
  if (row.budget != null && row.budget > 0) {
    parts.push(formatCents(row.budget))
  }
  return parts.join(' · ')
}

export const campaignResolver: MentionResolver = async (query, ctx: MentionContext) => {
  const { data, error } = await ctx.supabase
    .from('campaigns')
    .select('id, name, status, budget')
    .eq('workspace_id', ctx.workspaceId)
    .is('is_deleted', null)
    .order('updated_at', { ascending: false })
    .limit(200)

  if (error) return { ok: false, error: `Could not load campaigns: ${error.message}` }

  const rows = ((data ?? []) as CampaignRow[]).filter(r => r.name)
  if (rows.length === 0) return { ok: true, candidates: [] }

  const scored = rows
    .map(row => ({ row, score: scoreCampaign(row.name, query) }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.row.name.localeCompare(b.row.name))
    .slice(0, 8)

  const candidates: ResolvedMention[] = scored.map(({ row }) => ({
    kind: 'campaign',
    id: row.id,
    display: row.name,
    subtitle: makeSubtitle(row),
    href: `/campaigns/${row.id}`,
    data: { id: row.id, name: row.name, status: row.status, budget: row.budget },
  }))

  return { ok: true, candidates }
}
